import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import DashboardLayout from '../../../layouts/DashboardLayout';
import '../../../feature-styles/xsd.css';
import CertificateTypes from '../../certificate-types/pages/CertificateTypes/CertificateTypes';
import Xsd from './Xsd';
import { ENUM_CONFIGURATION_CERTIFICATES } from '../../certificate-types/interfaces/certificateTypes';
import { tabs } from '../../certificate-types/interfaces/certificateTypes';
import { xsdApi } from '../api/xsd.api';

const getDefaultBuilderVersion = (xsd) => {
  const versions = Array.isArray(xsd?.versions) ? xsd.versions : [];
  return versions.find((version) => String(version.version) === String(xsd.activeVersion))?.version || '';
};

function CertificateConfiguration() {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(() => location.state?.activeTab || tabs[0].name);
  const [certificateTypeId, setCertificateTypeId] = useState(null);
  const [builderXsds, setBuilderXsds] = useState([]);
  const [builderXsdId, setBuilderXsdId] = useState(() => String(location.state?.builderXsdId || ''));
  const [builderXsdVersion, setBuilderXsdVersion] = useState(() => String(location.state?.builderXsdVersion || ''));
  const [builderXsdsLoading, setBuilderXsdsLoading] = useState(false);

  useEffect(() => {
    if (activeTab !== ENUM_CONFIGURATION_CERTIFICATES.BUILDER) return undefined;

    let cancelled = false;
    setBuilderXsdsLoading(true);
    xsdApi.getAll()
      .then((response) => {
        if (!cancelled) setBuilderXsds(Array.isArray(response) ? response : []);
      })
      .catch((error) => {
        if (!cancelled) toast.error(error.message || 'No se pudieron cargar los XSD.');
      })
      .finally(() => {
        if (!cancelled) setBuilderXsdsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [activeTab]);

  const selectedBuilderXsd = builderXsds.find((xsd) => String(xsd.id) === builderXsdId);
  const builderXsdVersions = Array.isArray(selectedBuilderXsd?.versions) ? selectedBuilderXsd.versions : [];
  const selectedBuilderXsdVersion = builderXsdVersions.find(
    (version) => String(version.version) === builderXsdVersion,
  );

  useEffect(() => {
    if (!selectedBuilderXsd || selectedBuilderXsdVersion) return;
    setBuilderXsdVersion(getDefaultBuilderVersion(selectedBuilderXsd));
  }, [selectedBuilderXsd, selectedBuilderXsdVersion]);

  const openBuilderForXsd = (xsd, version) => {
    if (!xsd || !version) return;
    const query = new URLSearchParams({
      xsdId: String(xsd.id),
      xsdName: xsd.name || '',
      xsdVersion: String(version.version),
    });
    navigate(`/certificate-builder?${query.toString()}`, {
      state: {
        returnTo: location.pathname,
        returnTab: activeTab,
        returnBuilderXsdId: String(xsd.id),
        returnBuilderXsdVersion: String(version.version),
      },
    });
  };
  const handleBuilderXsdChange = (event) => {
    const nextXsdId = event.target.value;
    setBuilderXsdId(nextXsdId);
    const nextXsd = builderXsds.find((xsd) => String(xsd.id) === nextXsdId);
    setBuilderXsdVersion(nextXsd ? String(getDefaultBuilderVersion(nextXsd)) : '');
  };

  return (
    <DashboardLayout>
      <div className="page-container">
        <div className="page-header-row">
          <div className="page-title-wrapper">
            <p className="page-subtitle">Administración de configuraciones de certificados</p>
          </div>
        </div>

        <div className="tabs-bar">
          {tabs.map((tab) => (
            <button
              key={tab.name}
              type="button"
              onClick={() => {
                setActiveTab(tab.name);
                setCertificateTypeId(null);
              }}
              className={`tab-item ${activeTab === tab.name ? 'tab-item-active' : ''}`}
            >
              {tab.icon} {tab.name}
            </button>
          ))}
        </div>
        {activeTab === ENUM_CONFIGURATION_CERTIFICATES.CERTIFICADOS && (
          <CertificateTypes
            setCertificateTypeId={setCertificateTypeId}
            setActiveTab={setActiveTab}
          />
        )}
        {activeTab === ENUM_CONFIGURATION_CERTIFICATES.XSD && (
          <Xsd certificateTypeId={certificateTypeId} />
        )}
        {activeTab === ENUM_CONFIGURATION_CERTIFICATES.BUILDER && (
          <div>
            <div className="mb-3 flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 bg-white p-3">
              <label htmlFor="builder-xsd-select" className="text-sm font-semibold text-slate-700">
                XSD del certificado
              </label>
              <select
                id="builder-xsd-select"
                className="min-w-64 flex-1 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800"
                value={builderXsdId}
                disabled={builderXsdsLoading || builderXsds.length === 0}
                onChange={handleBuilderXsdChange}
              >
                <option value="">
                  {builderXsdsLoading ? 'Cargando XSD...' : 'Selecciona un XSD'}
                </option>
                {builderXsds.map((xsd) => (
                  <option key={xsd.id} value={String(xsd.id)}>{xsd.name}</option>
                ))}
              </select>
            </div>

            {selectedBuilderXsd && (
              <div className="mb-3 flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 bg-white p-3">
                <label htmlFor="builder-xsd-version-select" className="text-sm font-semibold text-slate-700">
                  Versión del XSD
                </label>
                <select
                  id="builder-xsd-version-select"
                  className="min-w-64 flex-1 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800"
                  value={builderXsdVersion}
                  disabled={builderXsdVersions.length === 0}
                  onChange={(event) => setBuilderXsdVersion(event.target.value)}
                >
                  <option value="">Selecciona una versión</option>
                  {builderXsdVersions.map((version) => (
                    <option key={version.version} value={String(version.version)}>
                      {version.version} ({version.status || 'PUBLISHED'})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {selectedBuilderXsd ? (
              <div className="rounded-lg border border-slate-200 bg-white p-4">
                <p className="mb-1 text-sm font-semibold text-slate-800">{selectedBuilderXsd.name}</p>
                <p className="mb-4 text-xs text-slate-500">
                  Cada versión conserva su diseño local en este navegador y usa su catálogo de campos. El editor se abrirá a pantalla completa.
                </p>
                <button
                  type="button"
                  className="rounded-md bg-[#273593] px-4 py-2 text-sm font-semibold text-white"
                  disabled={!selectedBuilderXsdVersion}
                  onClick={() => openBuilderForXsd(selectedBuilderXsd, selectedBuilderXsdVersion)}
                >
                  Abrir editor
                </button>
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-slate-300 bg-white px-5 py-10 text-center text-sm text-slate-500">
                {builderXsdsLoading ? 'Cargando XSD...' : 'Selecciona un XSD para crear o editar su certificado.'}
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

export default CertificateConfiguration;
